import { z } from 'zod';
import { emailSchema, fleetSlugSchema, idSchema } from './common.js';
import { vehicleTypeSchema } from './vehicle.js';
const fleetRoleSchema = z.enum(['owner', 'admin', 'dispatcher', 'driver', 'accountant']);
export const createFleetBodySchema = z.object({
    legalName: z.string().trim().min(1).max(160),
    tradingName: z.string().trim().max(160).nullable().optional(),
    slug: fleetSlugSchema,
    billingEmail: emailSchema,
    companyNumber: z.string().trim().max(20).nullable().optional(),
    vatNumber: z.string().trim().max(20).nullable().optional(),
    oLicenceNumber: z.string().trim().max(40).nullable().optional(),
});
const hexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Expected a #RRGGBB colour');
/** E.164: optional +, leading non-zero, 7–15 digits. Drives the portal phone. */
export const e164Schema = z
    .string()
    .trim()
    .regex(/^\+?[1-9]\d{6,14}$/, 'Phone must be E.164 (e.g. +441918330060)')
    .max(16);
export const updateFleetBodySchema = z
    .object({
    legalName: z.string().trim().min(1).max(160).optional(),
    tradingName: z.string().trim().max(160).nullable().optional(),
    /** Customer-facing brand, distinct from legal/trading (0045). */
    brandName: z.string().trim().min(1).max(160).nullable().optional(),
    companyNumber: z.string().trim().max(20).nullable().optional(),
    /**
     * M2a: a VAT-registered fleet CANNOT issue an invoice without this — the
     * database refuses it (BR104) and it prints on every VAT invoice. The
     * column has existed since the fleets table did and the repository has
     * always accepted it, but this .strict() schema omitted it, so nothing
     * could set it through the API and Settings → Company had no field. The
     * refusal pointed at a page that could not satisfy it.
     */
    vatNumber: z.string().trim().max(20).nullable().optional(),
    billingEmail: emailSchema.optional(),
    supportEmail: emailSchema.nullable().optional(),
    phoneE164: e164Schema.nullable().optional(),
    addressLine1: z.string().trim().max(160).nullable().optional(),
    addressLine2: z.string().trim().max(160).nullable().optional(),
    city: z.string().trim().max(80).nullable().optional(),
    postcode: z.string().trim().max(16).nullable().optional(),
    logoUrl: z.string().url().nullable().optional(),
    vatRegistered: z.boolean().optional(),
    brandPrimaryHex: hexColorSchema.nullable().optional(),
    brandAccentHex: hexColorSchema.nullable().optional(),
    /** Portal on/off — the U8 Settings toggle (owner-gated like the rest). */
    hasCustomerPortal: z.boolean().optional(),
})
    .strict();
// -------- Rate card (0046 / U9) --------
const tariffBandInputSchema = z
    .object({
    label: z.string().trim().min(1).max(80),
    vehicleTypes: z.array(vehicleTypeSchema).min(1).max(30),
    ratePerMilePence: z.number().int().min(0).max(1_000_000).nullable(),
    minimumPence: z.number().int().min(0).max(10_000_000).nullable(),
    pricedOnConfirmation: z.boolean(),
})
    .strict()
    .refine((b) => b.pricedOnConfirmation ||
    (b.ratePerMilePence !== null && b.minimumPence !== null), { message: 'Banded rows need both a rate and a minimum' });
/**
 * The whole rate card in one PUT (U9): scalars + the band ladder in display
 * order (array index = sort). One-band-per-vehicle-type is enforced in the
 * service — the schema can't see across rows.
 */
export const updateFleetTariffBodySchema = z
    .object({
    vatRatePercent: z.number().min(0).max(100),
    oohPercent: z.number().min(0).max(100),
    oohStartHour: z.number().int().min(0).max(23),
    oohEndHour: z.number().int().min(0).max(23),
    oohWeekends: z.boolean(),
    oohBankHolidays: z.boolean(),
    roadFactor: z.number().gt(0).max(10),
    extraStopFeePence: z.number().int().min(0).max(1_000_000),
    waitingRatePence: z.number().int().min(0).max(1_000_000),
    waitingFreeMinutes: z.number().int().min(0).max(1440),
    surcharges: z
        .array(z
        .object({
        label: z.string().trim().min(1).max(80),
        amountPence: z.number().int().min(0).max(10_000_000),
    })
        .strict())
        .max(20),
    bands: z.array(tariffBandInputSchema).min(1).max(20),
})
    .strict();
/** One lowercase DNS label (D5) — the <slug>.portal.breakernetwork.app part. */
export const updatePortalSlugBodySchema = z
    .object({
    portalSlug: z
        .string()
        .trim()
        .toLowerCase()
        .min(1)
        .max(63)
        .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Letters, digits and inner hyphens only')
        .nullable(),
})
    .strict();
/** Fleet logo upload cap — a logo, not a photo archive. */
export const FLEET_LOGO_MAX_BYTES = 2 * 1024 * 1024;
export const fleetLogoPresignBodySchema = z
    .object({
    mimeType: z.enum(['image/png', 'image/jpeg', 'image/webp']),
    sizeBytes: z.number().int().positive().max(FLEET_LOGO_MAX_BYTES),
})
    .strict();
export const driverEngagementTypeSchema = z.enum(['subcontractor', 'employed']);
/** Team unit: the badges an invite or toggle may grant. Owner is never
 *  invitable and never toggle-able (singular per fleet; transfer = future). */
export const invitableBadgeSchema = z.enum(['admin', 'dispatcher', 'accountant', 'driver']);
export const updateMemberBodySchema = z
    .object({
    /** Legacy single-role patch — accepted for one release (cached SPAs);
     *  the server maps it to roles=[role]. Prefer `roles`. */
    role: fleetRoleSchema.optional(),
    /** Team unit: the full badge set to hold after this patch. The Owner
     *  badge is server-managed — including or omitting it on the owner row
     *  is rejected/ignored per the immutability rule. */
    roles: z.array(invitableBadgeSchema).min(1).max(4).optional(),
    status: z.enum(['active', 'suspended']).optional(),
    employeeRef: z.string().trim().max(40).nullable().optional(),
    costCentre: z.string().trim().max(40).nullable().optional(),
    shiftPattern: z.string().trim().max(40).nullable().optional(),
    hourlyRatePence: z.number().int().min(0).nullable().optional(),
    /** 0049: null clears back to "not stated". */
    engagementType: driverEngagementTypeSchema.nullable().optional(),
    /** 0049: must be a fleet vehicle, pool-owned or owned by this driver. */
    defaultVehicleId: idSchema.nullable().optional(),
})
    .strict();
export const fleetIdParamSchema = z.object({
    fleetId: idSchema,
});
export const memberIdParamSchema = z.object({
    fleetId: idSchema,
    memberId: idSchema,
});
export const inviteIdParamSchema = z.object({
    fleetId: idSchema,
    inviteId: idSchema,
});
// -------- Setup wizard (signup) --------
/** The only step events the wizard may write; completion events stay
 *  server-derived (checklist_rates_confirmed / checklist_brand_set). */
export const SETUP_WIZARD_EVENTS = [
    'wizard_rates_started',
    'wizard_rates_skipped',
    'wizard_brand_started',
    'wizard_brand_skipped',
    'wizard_done_viewed',
    // Domain purchase-in-wizard (address step 3). fell_back is ALSO recorded
    // server-side by the order-cancel path so a user back-out is always
    // distinguishable from a registrar failure in the funnel.
    'wizard_domain_started',
    'wizard_domain_purchased',
    'wizard_domain_fell_back',
    'wizard_domain_byo_chosen',
    // Booking-page pre-warm: carries the real done-view → ready warm duration in
    // durationMs, so the funnel shows true cert/render timings.
    'wizard_booking_page_ready',
];
export const setupEventBodySchema = z
    .object({
    name: z.enum(SETUP_WIZARD_EVENTS),
    /** Only meaningful for wizard_booking_page_ready — the measured warm ms. */
    durationMs: z.number().int().nonnegative().max(3_600_000).optional(),
})
    .strict();
//# sourceMappingURL=fleet.js.map